import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { eur, useSettings } from "@/lib/api";
import { CartLine } from "@/components/CartDrawer";

export default function Cart() {
  const { items, subtotal } = useCart();
  const settings = useSettings();
  const methods = settings?.shipping?.methods || [];
  const [method, setMethod] = useState("");
  useEffect(() => {
    if (!method && methods[0]) setMethod(methods[0].id);
  }, [settings, method]); // eslint-disable-line react-hooks/exhaustive-deps

  const m = methods.find((x) => x.id === method);
  const threshold = settings?.shipping?.free_threshold;
  const allFree = items.every((i) => i.free_shipping);
  const shipping = !m || allFree || (threshold && subtotal >= threshold) ? 0 : m.price;

  if (!items.length)
    return (
      <div className="mx-auto max-w-3xl px-4 py-32 text-center" data-testid="cart-page-empty">
        <h1 className="font-display text-3xl font-bold text-[#0A0B0E]">Il carrello è vuoto</h1>
        <Link to="/catalogo" className="mt-8 inline-flex h-12 items-center rounded-full bg-[#0066FF] px-7 font-semibold text-white">Vai al catalogo</Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl px-4 pt-10 sm:px-6" data-testid="cart-page">
      <h1 className="font-display text-3xl font-bold tracking-[-0.03em] text-[#0A0B0E] sm:text-4xl">Carrello</h1>
      <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_380px]">
        <div className="divide-y divide-slate-100 rounded-3xl border border-slate-200 bg-white px-5">
          {items.map((l) => <CartLine key={l.key} line={l} />)}
        </div>
        <aside className="h-fit rounded-3xl border border-slate-200 bg-[#F5F7FA] p-6 text-slate-900 lg:sticky lg:top-36">
          <p className="mb-3 text-sm font-semibold">Spedizione</p>
          <div className="space-y-2">
            {methods.map((x) => (
              <label key={x.id} className={`flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3 text-sm transition-colors ${method === x.id ? "border-[#0066FF] bg-white" : "border-slate-200 bg-white/60"}`}>
                <span className="flex items-center gap-3"><input type="radio" checked={method === x.id} onChange={() => setMethod(x.id)} className="accent-[#0066FF]" data-testid={`shipping-method-${x.id}`} />{x.name}{x.days && <span className="text-xs text-slate-500">{x.days}</span>}</span>
                <span className="font-mono text-xs">{eur(allFree ? 0 : x.price)}</span>
              </label>
            ))}
          </div>
          <dl className="mt-6 space-y-2 border-t border-slate-200 pt-5 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Subtotale</dt><dd data-testid="cart-subtotal">{eur(subtotal)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Spedizione</dt><dd data-testid="cart-shipping">{eur(shipping)}</dd></div>
            <div className="flex justify-between pt-2 font-display text-xl font-semibold"><dt>Totale</dt><dd data-testid="cart-total">{eur(subtotal + shipping)}</dd></div>
          </dl>
          <Link to="/checkout" className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#0066FF] font-semibold text-white transition-colors hover:bg-[#1a75ff]" data-testid="cart-checkout-btn">
            <Lock className="h-4 w-4" /> Procedi al checkout
          </Link>
          {!settings?.payments_enabled && (
          <p className="mt-4 rounded-xl border border-[#0066FF]/20 bg-[#0066FF]/5 p-4 text-xs leading-relaxed text-[#0047B3]" data-testid="checkout-disabled-notice">
            I pagamenti online con Stripe saranno attivati a breve. Al momento non è possibile inviare ordini dal sito. Per informazioni <Link to="/contatti" className="underline">contattaci</Link>.
          </p>
          )}
        </aside>
      </div>
    </div>
  );
}
